import React, { useEffect, useMemo, useRef, useState } from 'react'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { AppSelect as Select } from '../../../../../../components/Select'
import { getShortfallTimeseries } from '../core/api'
import { ShortfallOutcome, ShortfallTimeseriesBucket } from '../core/types'
import { METRIC_HINTS } from '../core/hints'
import { SHORTFALL_OUTCOME_OPTIONS } from '../core/consts'
import { useChartTheme } from '../core/chartTheme'
import { buildTooltipHtml, mergeReasons, moneyShort, TooltipSection } from '../core/shortfallTooltip'
import {
  buildBasketCells,
  buildColumnSeries,
  buildHeatSeries,
  cellPaint,
  maxCount as maxCellCount,
  Side,
  totalEvents as countEvents,
} from '../core/shortfallSeries'
import { heatSplitEvents, mergeChartEvents, paintHeatCells } from '../core/heatSplit'
import { stackedBarRoundingEvents } from '../core/barRounding'
import { hourlyAxis, dayDividerEvents } from '../core/timeAxis'
import { getMerchantLookup, getPaymentMethodLookup } from '../../../../../common/core/_requests'
import InfoHint from '../../../../../common/InfoHint'

type View = 'heatmap' | 'columns'
type Option = { value: number; label: string }

const HEAT_HEIGHT = 300

// Непокрытый спрос shared-пула за последние 24 часа, две серии на одних осях:
// депозиты, не нашедшие выплату, и выплаты, не нашедшие депозит (CPAYMENT-1676).
// Два вида поверх одних данных: кубики (штуки, ячейка поделена пополам) и
// колонки (деньги, две стековые группы рядом). Фильтры «Мерчант» и «Платёжный
// метод» сужают обе серии сразу; «Режим» переключает только цифру выплат.
const ShortfallChart: React.FC<{ currency: string }> = ({ currency }) => {
  const [buckets, setBuckets] = useState<ShortfallTimeseriesBucket[]>([])
  const [view, setView] = useState<View>('heatmap')
  const [merchant, setMerchant] = useState<Option | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<Option | null>(null)
  const [outcome, setOutcome] = useState<ShortfallOutcome>('all')
  const [merchantOptions, setMerchantOptions] = useState<Option[]>([])
  const [methodOptions, setMethodOptions] = useState<Option[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const theme = useChartTheme()
  const chartBox = useRef<HTMLDivElement | null>(null)

  // Варианты фильтров — из общих лукапов (value = внутренний id), а не из
  // активности окна. Роли лукапов обязаны покрывать роли дашборда: 403 здесь не ломает график,
  // а молча оставляет выпадашку пустой (из-за этого /api/merchant/lookup открыт оператору
  // платежей мерчанта в CPAYMENT-1814).
  useEffect(() => {
    let cancelled = false

    getMerchantLookup()
      .then((resp) => {
        if (cancelled || !resp.data?.success) return
        setMerchantOptions(resp.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))

    getPaymentMethodLookup()
      .then((resp) => {
        if (cancelled || !resp.data?.success) return
        setMethodOptions(resp.data.data ?? [])
      })
      .catch(() => setMethodOptions([]))

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getShortfallTimeseries(currency, {
      merchant: merchant?.value,
      paymentMethod: paymentMethod?.value,
      outcome,
    })
      .then((resp) => {
        if (cancelled) return
        if (resp.data.success && resp.data.data) {
          setBuckets(resp.data.data.buckets ?? [])
          setError(null)
        } else {
          setError('Не удалось загрузить отчёт')
        }
      })
      .catch(() => !cancelled && setError('Сервис мэтчинга недоступен'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [currency, merchant, paymentMethod, outcome])

  // ВСЁ, что уезжает в <Chart>, обязано быть мемоизировано и меняться только
  // вместе с данными. Обёртка react-apexcharts сравнивает опции со СВОИМИ
  // внутренними через JSON.stringify — они не совпадают никогда, поэтому на
  // каждый новый объект опций она делает полный updateOptions. При опросе
  // дашборда это срывало анимацию входа (ячейки застывали недорисованными) и
  // дёргало ховер. Идентичность объектов — единственная защита.
  const axis = useMemo(() => hourlyAxis(buckets.map((b) => b.ts)), [buckets])
  const cells = useMemo(() => buildBasketCells(buckets), [buckets])
  const totalEvents = useMemo(() => countEvents(cells), [cells])
  const maxCount = useMemo(() => maxCellCount(cells), [cells])
  const { series: heatSeries, rows: heatRows } = useMemo(() => buildHeatSeries(cells), [cells])
  const { series: columnSeries, colors: columnColors } = useMemo(
    () => buildColumnSeries(cells),
    [cells]
  )

  // Обе половины ячейки рисуем поверх графика — ячейка heatmap у Apex
  // одноцветная по устройству (см. core/heatSplit.ts).
  const paintFor = useMemo(
    () => (seriesIndex: number, hour: number) =>
      cellPaint(cells, heatRows, maxCount, theme.gridColor, seriesIndex, hour),
    [cells, heatRows, maxCount, theme.gridColor]
  )

  // Обязательный путь перерисовки половин: следим за самим SVG. На события Apex
  // полагаться нельзя — `updated` приходит не на каждом пути обновления, а
  // отрисовка ячеек к моменту события может быть ещё не закончена. Наблюдатель
  // отключается на время нашей же отрисовки, иначе она вызовет саму себя.
  useEffect(() => {
    const box = chartBox.current
    if (view !== 'heatmap' || !box) return

    let frame = 0
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame)
      // Ячейки Apex мог и не менять (ховер, тултип) — тогда отрисовка сама себя
      // отменит по метке на ячейке; принудительно перерисовываем только на смене
      // данных или темы, то есть на прогоне эффекта.
      frame = requestAnimationFrame(() => repaint(false))
    })
    const repaint = (force: boolean) => {
      observer.disconnect()
      paintHeatCells(box, paintFor, force)
      observer.observe(box, { childList: true, subtree: true })
    }

    repaint(true)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [view, paintFor, loading, error])

  const { heatmapOptions, columnsOptions } = useMemo(() => {
    const dayDividers = dayDividerEvents(axis.dayStarts, theme.dividerColor)
    const splitEvents = heatSplitEvents(paintFor)

    const hourTitle = (pointIndex: number): string => {
      const c = axis.categories[pointIndex]
      return Array.isArray(c) ? c.join(' ') : c ?? ''
    }
    // Ячейка кубиков — одна корзина, колонка — весь час: итог и причины берутся
    // по тем корзинам, на которые наведён курсор.
    const section = (side: Side, baskets: number[], pointIndex: number): TooltipSection => ({
      side,
      total: {
        count: baskets.reduce((sum, n) => sum + (cells[n][side].counts[pointIndex] ?? 0), 0),
        amount: baskets.reduce((sum, n) => sum + (cells[n][side].amounts[pointIndex] ?? 0), 0),
      },
      reasons: mergeReasons(baskets.map((n) => cells[n][side].reasons[pointIndex] ?? [])),
    })
    const allBaskets = cells.map((_, n) => n)

    // Тултип пересобирается на каждое движение мыши, а разметка зависит только от
    // часа (и от ряда у кубиков) — поэтому кэшируем её на срез данных.
    const tooltipCache = new Map<string, string>()
    const cached = (key: string, build: () => string) => {
      const hit = tooltipCache.get(key)
      if (hit !== undefined) return hit
      const html = build()
      tooltipCache.set(key, html)
      return html
    }

    const columnsTooltip = ({ dataPointIndex }: { dataPointIndex: number }) =>
      cached(`c:${dataPointIndex}`, () =>
        buildTooltipHtml(hourTitle(dataPointIndex), [
          section('deposit', allBaskets, dataPointIndex),
          section('payout', allBaskets, dataPointIndex),
        ])
      )

    // Наведение на ячейку: обе стороны этой корзины в этот час.
    const heatmapTooltip = ({
      seriesIndex,
      dataPointIndex,
    }: {
      seriesIndex: number
      dataPointIndex: number
    }) => {
      const row = heatRows[seriesIndex]
      if (!row) return ''
      return cached(`h:${seriesIndex}:${dataPointIndex}`, () =>
        buildTooltipHtml(`${hourTitle(dataPointIndex)} · ${cells[row.basketIndex].basket}`, [
          section('deposit', [row.basketIndex], dataPointIndex),
          section('payout', [row.basketIndex], dataPointIndex),
        ])
      )
    }

    const heatmap: ApexOptions = {
      chart: {
        type: 'heatmap',
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        // Анимация входа принадлежит графику и обязана быть задана явно: у Apex
        // она срывается любым лишним проходом рендера. redrawOnParentResize
        // (по умолчанию включён) пересобирает график по ресайзу карточки — тот
        // проход идёт уже с resized=true, то есть БЕЗ анимации, и ячейки просто
        // «загораются». Ресайз окна по-прежнему обрабатывается.
        animations: {
          enabled: true,
          speed: 500,
          animateGradually: { enabled: false },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
        redrawOnParentResize: false,
        events: mergeChartEvents(dayDividers, splitEvents),
      },
      theme: { mode: theme.mode },
      // Числа рисуются вместе со своими половинами (core/heatSplit.ts) — иначе
      // подпись Apex сидела бы по центру ячейки, ровно на стыке половин.
      dataLabels: { enabled: false },
      // Сама ячейка — нейтраль темы: обе половины поверх неё наши. Одна серия —
      // один цвет, поэтому цвет задаётся списком по числу рядов.
      colors: heatSeries.map(() => theme.gridColor),
      plotOptions: {
        heatmap: {
          radius: 3,
          enableShades: false,
        },
      },
      grid: { borderColor: theme.gridColor },
      xaxis: {
        type: 'category',
        categories: axis.categories,
        labels: { rotate: -45, style: { fontSize: '10px' } },
        crosshairs: { show: false },
        // Тултип оси у Apex полупрозрачный и наезжает на подпись под ним.
        tooltip: { enabled: false },
      },
      // Оба вида делят один инстанс графика, поэтому каждый обязан задать yaxis
      // целиком: у кубиков по этой оси идут НАЗВАНИЯ корзин, и денежный форматтер
      // с заголовком, оставшиеся от колонок, превращают их в NaN.
      yaxis: {
        show: true,
        title: { text: '' },
        labels: { formatter: (v: unknown) => (v === undefined || v === null ? '' : String(v)) },
      },
      // Дефолтная заливка Apex — 0.85: нейтральная ячейка под нашими половинами
      // не должна их приглушать.
      fill: { opacity: 1 },
      // Обводка ячейки и есть промежуток между кубиками — красим её фоном
      // карточки. Задавать ОБЯЗАТЕЛЬНО здесь: инстанс графика общий с колонками,
      // и их прозрачная обводка иначе остаётся в силе, а сетка кубиков
      // сливается в сплошное серое поле.
      stroke: { show: true, width: 2, colors: [theme.cardBg] },
      tooltip: { custom: heatmapTooltip },
      legend: { show: false },
    }

    const columns: ApexOptions = {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        // У сгруппированного стека анимацию входа приходится выключать: в Safari
        // части столбцов на ней «улетают» ниже нуля и встают на место только при
        // следующей перерисовке (наведении). Ставить её обратно можно, только
        // проверив вид в Safari. redrawOnParentResize — по той же причине, что и
        // у кубиков: лишний проход пересборки.
        animations: { enabled: false },
        redrawOnParentResize: false,
        events: mergeChartEvents(dayDividers, stackedBarRoundingEvents()),
      },
      theme: { mode: theme.mode },
      colors: columnColors,
      // 65% — прежняя ширина одиночной колонки: у Apex это ширина ВСЕЙ группы в
      // слоте часа, поэтому пара занимает ровно тот же след, что раньше одна.
      // borderRadius: 0 — скругления рисует stackedBarRoundingEvents по единому
      // правилу для обеих групп (см. core/barRounding.ts). Апексовы варианты
      // (`around` / `end` / `borderRadiusWhenStacked`) в сгруппированном стеке
      // садятся на разные корзины в разных группах — проверено замером путей.
      plotOptions: { bar: { columnWidth: '65%', borderRadius: 0 } },
      // Зазор между корзинами внутри стека — это ОБВОДКА столбца: Apex укорачивает
      // каждый сегмент на её ширину (по умолчанию 2px). Скругление тут ни при чём.
      // Цвет прозрачный, иначе в тёмной теме на стыках будут белые полосы.
      stroke: { show: true, width: 1, colors: ['transparent'] },
      // Дефолтная заливка столбца у Apex — 0.85, и тона корзин выцветают. Нам
      // нужны ровно те цвета, что заданы рампой.
      fill: { opacity: 1 },
      // Подсветка — только на весь час (полоса crosshairs): тултип суммарный по
      // колонке, поэтому подсвечивать отдельный сегмент корзины незачем — это
      // лишь путает, показывая одну корзину при данных по всем.
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } },
      },
      dataLabels: { enabled: false },
      grid: { borderColor: theme.gridColor },
      xaxis: {
        categories: axis.categories,
        labels: { rotate: -45, style: { fontSize: '10px' } },
        // Полоса подсветки наведённого часа. С парой колонок на час она несёт
        // смысл: тултип общий, и без полосы не видно, к какому часу он относится
        // (сама подсветка столбца на группах теряется).
        crosshairs: {
          show: true,
          width: 'tickWidth',
          opacity: 0.35,
          fill: { type: 'solid', color: theme.gridColor },
          stroke: { width: 0 },
        },
        // Тултип оси у Apex полупрозрачный и наезжает на подпись под ним; час
        // есть в заголовке основного тултипа.
        tooltip: { enabled: false },
      },
      // Высота колонки — деньги: час с одним крупным депозитом стоит дороже часа с
      // четырьмя мелкими, и по количеству это не видно. Количество — в тултипе.
      // min не задаём: стек неотрицательный, Apex и так начинает с нуля, а любое
      // заданное здесь значение переехало бы в кубики при переключении вида.
      yaxis: {
        show: true,
        title: { text: 'Непокрытая сумма' },
        labels: { formatter: (v: number) => moneyShort(v) },
      },
      tooltip: { shared: true, intersect: false, custom: columnsTooltip },
      // markers.radius исчез в Apex 3.5x — форма маркера задаётся через shape.
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        markers: { shape: 'square' },
      },
    }

    return { heatmapOptions: heatmap, columnsOptions: columns }
  }, [axis, cells, heatRows, heatSeries, maxCount, columnColors, paintFor, theme])

  // Селекты живут в шапке карточки рядом с переключателем вида; ширину задаём
  // явно, иначе react-select схлопывается по длине плейсхолдера.
  const selectStyles = {
    container: (base: Record<string, unknown>) => ({ ...base, minWidth: 190 }),
    menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 5 }),
  }

  return (
    <div className='card mb-5'>
      <div className='card-header d-flex flex-wrap justify-content-between align-items-center gap-3'>
        <h3 className='card-title d-inline-flex align-items-center'>
          Непокрытый спрос: депозиты без выплаты и выплаты без депозита
          <InfoHint text={METRIC_HINTS.shortfall} />
        </h3>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <Select<Option>
            className='form-select-sm p-0'
            classNamePrefix='select'
            styles={selectStyles}
            options={merchantOptions}
            value={merchant}
            onChange={(o) => setMerchant(o)}
            isClearable
            placeholder='Мерчант: все'
            noOptionsMessage={() => 'Нет вариантов'}
          />
          <Select<Option>
            className='form-select-sm p-0'
            classNamePrefix='select'
            styles={selectStyles}
            options={methodOptions}
            value={paymentMethod}
            onChange={(o) => setPaymentMethod(o)}
            isClearable
            placeholder='Платёжный метод: все'
            noOptionsMessage={() => 'Нет вариантов'}
          />
          {/* Режим — переключатель одной цифры, а не фильтр: «Все» это значение,
              а не его отсутствие, поэтому селект не очищаемый. */}
          <Select<{ label: string; value: ShortfallOutcome }>
            className='form-select-sm p-0'
            classNamePrefix='select'
            styles={selectStyles}
            options={SHORTFALL_OUTCOME_OPTIONS}
            value={SHORTFALL_OUTCOME_OPTIONS.find((o) => o.value === outcome) ?? null}
            onChange={(o) => setOutcome(o?.value ?? 'all')}
            isSearchable={false}
            aria-label='Режим цифры выплат'
          />
          <div className='btn-group btn-group-sm'>
            <button
              className={`btn btn-sm ${view === 'heatmap' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setView('heatmap')}
              title='Кубики: сумма × час, ячейка поделена пополам (верх — депозиты)'
            >
              Кубики
            </button>
            <button
              className={`btn btn-sm ${view === 'columns' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setView('columns')}
              title='Колонки: две группы рядом, итог по часам'
            >
              Колонки
            </button>
          </div>
        </div>
      </div>
      {/* Высота тела карточки зафиксирована: виды разной высоты иначе двигали бы
          страницу под курсором при переключении. */}
      <div className='card-body' style={{ minHeight: 340 }}>
        {loading && <div className='text-muted'>Загрузка…</div>}
        {error && <div className='alert alert-danger'>{error}</div>}
        {!loading &&
          !error &&
          (totalEvents === 0 ? (
            <div className='text-muted'>
              За последние 24 часа по этому срезу всё сводилось: депозиты находили выплату, выплаты
              — депозит.
            </div>
          ) : (
            <div ref={chartBox}>
              {view === 'heatmap' ? (
                // Оба вида делят один инстанс графика: `key` на вид не вешать —
                // при пересоздании контейнер на кадр пуст (Apex рисует
                // асинхронно), страница проседает и прокрутку отбрасывает вверх.
                <Chart
                  options={heatmapOptions}
                  series={heatSeries}
                  type='heatmap'
                  height={HEAT_HEIGHT}
                />
              ) : (
                <Chart options={columnsOptions} series={columnSeries} type='bar' height={340} />
              )}
            </div>
          ))}
      </div>
    </div>
  )
}

export default ShortfallChart
